import {buildOrgSearchUrl} from "./buildOrgSearchUrl";
import type {CommonOrgEntity} from "./MembershipData";
import {isCommonOrgEntity} from "./MembershipData";
import type {OrgSearchFn} from "./orgSearch";
import {SEARCH_RESULT_LIMIT} from "./orgSearch";

/**
 * Response body of `GET /context/orgs/search/mine` — the entities matching a query.
 *
 * An envelope rather than a bare array, mirroring the BFF, so a later addition (paging, a "why
 * this matched" hint) is a new field rather than a change of the top-level type.
 */
interface CommonOrgSearchResults {
  entities: CommonOrgEntity[];
}

/**
 * Runtime type guard for that body. A TypeScript `as` cast verifies nothing at runtime, and a
 * malformed entity here would propagate `displayName: undefined` into the
 * `bridge-bar-context-changed` payload of every adopting surface the moment the user picked it.
 *
 * Rejecting is safe in a way it is not for the membership response: a rejected search renders
 * the selector's retry state, whereas a rejected membership response would take the whole
 * selector to its error state. So this validates strictly and does not tolerate an unexpected
 * shape.
 */
function isCommonOrgSearchResults(
  value: unknown
): value is CommonOrgSearchResults {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as CommonOrgSearchResults;
  return (
    Array.isArray(candidate.entities) &&
    candidate.entities.every(isCommonOrgEntity)
  );
}

/** What a search needs to know to reach the BFF, resolved per call from the bar's contexts. */
export interface OrgSearchTarget {
  /** The Bridge Bar API base URL, from the runtime environment. */
  baseUrl: string;
  /** `PROD` or `NON_PROD` — see {@link toPartitionQueryParameter}. */
  partitionQueryParameter: string;
}

/**
 * Searches the entity hierarchy through the Bridge Bar API.
 *
 * The target is resolved per call rather than captured once, because the bar's runtime arrives
 * asynchronously and can change afterwards (an adopting page may switch partition without
 * remounting). A search issued before the runtime resolves rejects, which the selector renders
 * as its retry state — the honest outcome, since it cannot know where to ask.
 *
 * Rejects on anything that is not a well-formed 2xx: a non-2xx status, an unparseable body, or a
 * body of the wrong shape. Every one of those is a failed search, and the selector has exactly
 * one state for it. `credentials: "include"` sends the Bridge session cookie, which is the only
 * thing identifying the user — the endpoint refuses a caller it cannot put in search mode.
 */
export function createOrgSearch(
  resolveTarget: () => OrgSearchTarget | undefined
): OrgSearchFn {
  return async (query, signal) => {
    const target = resolveTarget();
    if (!target) {
      throw new Error(
        "Cannot search for entities: the Bridge Bar API URL is not resolved yet"
      );
    }

    const url = buildOrgSearchUrl(
      target.baseUrl,
      target.partitionQueryParameter,
      query,
      SEARCH_RESULT_LIMIT
    );

    const response = await fetch(url, {
      credentials: "include",
      mode: "cors",
      signal,
      headers: {
        "Accept": "application/vnd.coxauto.v1+json",
        "X-CoxAuto-Correlation-Id": crypto.randomUUID()
      }
    });

    if (!response.ok) {
      throw new Error(
        `Failed to search entities: ${response.status} ${response.statusText}`
      );
    }

    const parsed: unknown = await response.json();
    if (!isCommonOrgSearchResults(parsed)) {
      throw new Error(
        "Malformed entity search response: expected { entities: CommonOrgEntity[] }"
      );
    }

    return parsed.entities;
  };
}
